<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useClipboard } from '@vueuse/core';
import { LocalStorage } from 'shared/helpers/localStorage';
import { useMapGetter } from 'dashboard/composables/store';
import { useMobileAppInstall } from 'dashboard/composables/useMobileAppInstall';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ userId: { type: Number, required: true } });
const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');
const dialog = ref(null);
const copyError = ref(false);
const {
  canOfferInstall,
  canPrompt,
  visible,
  instructions,
  pending,
  installError,
  isIos,
  isSafari,
  isChrome,
  openInstall,
  closeInstall,
  install,
} = useMobileAppInstall();
const appName = computed(() => globalConfig.value.installationName);
const appUrl = new URL('/app/', window.location.origin).href;
const { copy, copied, isSupported } = useClipboard({ source: appUrl });
const needsOtherBrowser = isIos ? !isSafari : !isChrome;
const steps = computed(() => {
  if (isIos) {
    return [
      ...(isSafari ? [] : ['IOS_OPEN']),
      'IOS_SHARE',
      'IOS_ADD',
      'IOS_CONFIRM',
    ];
  }
  return [
    ...(isChrome ? [] : ['ANDROID_OPEN']),
    'ANDROID_MENU',
    'ANDROID_INSTALL',
  ];
});
const primaryLabel = computed(() => {
  if (instructions.value) return t('MOBILE_APP_INSTALL.DONE');
  return t(
    canPrompt.value || isChrome
      ? 'MOBILE_APP_INSTALL.INSTALL'
      : 'MOBILE_APP_INSTALL.SHOW_INSTRUCTIONS'
  );
});
const primary = () => {
  if (instructions.value) closeInstall();
  else install();
};
const copyAddress = async () => {
  copyError.value = false;
  try {
    await copy();
  } catch {
    copyError.value = true;
  }
};

watch(
  [() => props.userId, canOfferInstall, canPrompt],
  ([userId, available, promptAvailable]) => {
    if (!userId || !available) return;
    if (isChrome && !promptAvailable) return;
    const key = `guandalini:mobile-install:shown:${userId}`;
    if (LocalStorage.get(key)) return;
    LocalStorage.set(key, true);
    openInstall();
  },
  { immediate: true }
);
watch(
  visible,
  async shown => {
    if (!shown) return;
    copyError.value = false;
    await nextTick();
    dialog.value?.open();
  },
  { immediate: true }
);
onUnmounted(closeInstall);
</script>

<template>
  <Dialog
    v-if="visible"
    ref="dialog"
    type="edit"
    :title="t('MOBILE_APP_INSTALL.TITLE')"
    overflow-y-auto
    @confirm="primary"
    @close="closeInstall"
  >
    <div class="flex min-w-0 flex-col gap-4">
      <span
        aria-hidden="true"
        class="grid size-12 place-items-center rounded-2xl bg-n-teal-3 text-n-teal-11"
      >
        <span class="i-lucide-smartphone size-6" />
      </span>
      <p class="m-0 text-sm leading-6 text-n-slate-12">
        {{ t('MOBILE_APP_INSTALL.DESCRIPTION', { app: appName }) }}
      </p>
      <template v-if="instructions">
        <p
          v-if="installError"
          role="status"
          class="m-0 text-sm text-n-slate-11"
        >
          {{ t('MOBILE_APP_INSTALL.MANUAL_INSTALL') }}
        </p>
        <ol
          class="m-0 flex list-decimal flex-col gap-3 ps-5 text-sm text-n-slate-12"
        >
          <li v-for="step in steps" :key="step">
            {{ t(`MOBILE_APP_INSTALL.STEPS.${step}`, { app: appName }) }}
          </li>
        </ol>
        <div v-if="needsOtherBrowser" class="flex min-w-0 flex-col gap-2">
          <p
            class="m-0 break-all rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11"
          >
            {{ appUrl }}
          </p>
          <Button
            v-if="isSupported"
            type="button"
            color="slate"
            variant="faded"
            icon="i-lucide-copy"
            :label="
              t(
                copied
                  ? 'MOBILE_APP_INSTALL.COPIED'
                  : 'MOBILE_APP_INSTALL.COPY_ADDRESS'
              )
            "
            @click="copyAddress"
          />
          <p v-if="copyError" role="alert" class="m-0 text-xs text-n-ruby-11">
            {{ t('MOBILE_APP_INSTALL.COPY_FAILED') }}
          </p>
        </div>
      </template>
    </div>
    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          v-if="!instructions"
          type="button"
          color="slate"
          variant="faded"
          class="w-full sm:w-auto"
          :label="t('MOBILE_APP_INSTALL.LATER')"
          @click="closeInstall"
        />
        <Button
          type="button"
          class="w-full sm:w-auto"
          :label="primaryLabel"
          :disabled="pending"
          :is-loading="pending"
          @click="primary"
        />
      </div>
    </template>
  </Dialog>
</template>
